/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { CardListSkeleton } from './skeletons';
import { DataStateError } from './DataStateError';
import {
  useDataStateOverride,
  __setDataStateOverrideForTest,
} from './use-data-state-override';
import { useContentLoadingPill } from './use-content-loading-pill';

/**
 * Test-only re-export of {@link __setDataStateOverrideForTest} kept under the
 * historical name so existing `DataState` tests inject the simulator module the
 * same way.
 */
export const __setDataStateSimulatorForTest = __setDataStateOverrideForTest;

export interface DataStateProps {
  /** True while the first data fetch has not resolved. Gates every other state. */
  isLoading: boolean;
  /** True when the resolved fetch failed. */
  isError?: boolean;
  /** True when the resolved data set is empty. Only honored once loaded. */
  isEmpty: boolean;
  /** Invoked when the user clicks retry on the error state. */
  onRetry?: () => void;
  /** Custom loading visual; defaults to a generic card-list skeleton. */
  loadingFallback?: React.ReactNode;
  /** Custom error visual; the shared error visual is shown when omitted. */
  errorState?: React.ReactNode;
  /**
   * Message rendered by the built-in error visual when no custom `errorState`
   * is supplied. Defaults to a generic Portuguese copy.
   */
  errorMessage?: string;
  /** Visual rendered alongside children when loaded and empty. */
  emptyState?: React.ReactNode;
  /** The actual content, shown once loaded without error. */
  children: React.ReactNode;
}

const DEFAULT_ERROR_MESSAGE = 'Não foi possível carregar os dados.';

/**
 * Renders the error branch of {@link DataState}: either the caller-provided
 * `errorState` node or a default message with an optional retry action.
 *
 * @param errorState Custom error node, or falsy to use the default message.
 * @param onRetry Optional retry callback rendered as a button.
 * @param errorMessage The default message shown when no `errorState` is given.
 * @returns The error UI element.
 */
function renderError(
  errorState: React.ReactNode,
  onRetry: (() => void) | undefined,
  errorMessage: string,
): React.ReactElement {
  if (errorState) {
    return (
      <div data-testid="data-state-error" className="flex min-h-0 flex-1 flex-col">
        {errorState}
      </div>
    );
  }
  return (
    <div data-testid="data-state-error" className="flex min-h-0 flex-1 flex-col">
      <DataStateError
        message={errorMessage}
        onRetry={onRetry}
        data-testid="data-state-error-fallback"
      />
    </div>
  );
}

/**
 * Single gate for the loading / error / empty / content lifecycle of any
 * data-dependent view. While loading it renders only the skeleton, never the
 * empty or zero state.
 *
 * @param props Lifecycle flags, optional state visuals and the content.
 * @returns The visual matching the current data lifecycle state.
 */
export const DataState: React.FC<DataStateProps> = ({
  isLoading,
  isError = false,
  isEmpty,
  onRetry,
  loadingFallback,
  errorState,
  errorMessage = DEFAULT_ERROR_MESSAGE,
  emptyState,
  children,
}) => {
  const sim = useDataStateOverride();
  const isLoadingState = isLoading || sim === 'loading';
  useContentLoadingPill(isLoadingState);
  if (isLoadingState) {
    return (
      <div data-testid="data-state-loading" className="flex min-h-0 flex-1 flex-col">
        {loadingFallback ?? <CardListSkeleton count={3} />}
      </div>
    );
  }

  if (isError || sim === 'error') {
    return renderError(errorState, onRetry, errorMessage);
  }

  return (
    <>
      {children}
      {(isEmpty || sim === 'empty') && emptyState && (
        <div data-testid="data-state-empty">{emptyState}</div>
      )}
    </>
  );
};
